import AdminAPI from 'config/api/AdminAPI';
import AuthAPI from 'config/api/AuthAPI';
import {
  ADMIN_ROLE_SLUG,
  APP_CODE,
  CUSTOMER_ROLE_SLUG,
  DEVICE_TYPE,
  FFL_ROLE_SLUG,
} from 'config/app.config';
import { placeParamsToURL } from 'helpers/CommonHelper';
import { getServerFormattedDate } from 'helpers/DateHelper';
import { notify } from 'helpers/NotifyHelper';
import {
  getDeviceId,
  removeGuestDeliveryLocation,
  removeSelectedFflCoupons,
  storeUserInfo,
} from 'helpers/StorageHelper';
import { largeSuccessMessageWithHTML, successMessageWithHTML } from 'helpers/SwalAlertHelper';
import { FFLNumberCleanREGEX } from 'helpers/ValidationHelper';
import { t } from 'i18next';
import {
  clearSidebarOrderMap,
  saveSidebarOrderMap,
} from 'modules/admin/components/Navbars/sidebarConfig';
import { adminMainRoutes } from 'routes/admin/AdminRoutes';
import { customerMainRoutes } from 'routes/customer/CustomerRoutes';
import apiService from 'services/api.service';
import { store } from 'store';
import { getFFLProfileInfo, verifyFFLLicence } from 'store/admin/actions/AdminFFLAction';
import { setCheckoutSelectedShippingFfl } from 'store/customer/slices/CustomerCheckoutSlice';

import { userInfoSelector } from '../selectors/authSelectors';
import { logoutUserState, setUserAuthInfo } from '../slices/AuthSlice';

import { manageAPICall } from './CommonCrudAction';

export const updateUserState = (userInfo: any) => {
  store.dispatch(setUserAuthInfo(userInfo));
};
export const customerQuickRegister = async (data: object | null) => {
  const response = await manageAPICall(
    AuthAPI.customerQuickRegister.url,
    AuthAPI.customerQuickRegister.method,
    data
  );
  return response;
};
export const loginUser = async (data: object | null) => {
  const response = await manageAPICall(AuthAPI.login.url, AuthAPI.login.method, data);
  return response;
};
export const getAdminProfileInfo = async (token = null) => {
  let response;
  if (token) {
    response = response = await apiService.getPublicTokenData(
      AdminAPI.getAdminFFLProfile.url,
      {},
      token
    );
  } else {
    response = await manageAPICall(
      AdminAPI.getAdminFFLProfile.url,
      AdminAPI.getAdminFFLProfile.method,
      {}
    );
  }
  return response;
};
export const verifyAccountAPI = async (data: object) => {
  const response = await manageAPICall(
    AuthAPI.verifyAccount.url,
    AuthAPI.verifyAccount.method,
    data
  );
  return response;
};
export const getProfileDetails = async () => {
  const response = await manageAPICall(AuthAPI.getProfile.url, AuthAPI.getProfile.method, {});
  return response;
};
export const updateProfileDetails = async (data: object) => {
  // update profile of logged in user
  const response = await manageAPICall(
    AuthAPI.updateProfile.url,
    AuthAPI.updateProfile.method,
    data
  );
  return response;
};
export const deleteAccount = async (data: object) => {
  const response = await manageAPICall(
    AuthAPI.deleteAccount.url,
    AuthAPI.deleteAccount.method,
    data
  );
  return response;
};
export const updatePasswordDetails = async (data: object) => {
  const response = await manageAPICall(
    AuthAPI.updatePassword.url,
    AuthAPI.updatePassword.method,
    data
  );
  return response;
};
export const forgotPasswordCall = async (data: object) => {
  const response = await manageAPICall(
    AuthAPI.forgotPassword.url,
    AuthAPI.forgotPassword.method,
    data
  );
  return response;
};
export const logoutUser = async () => {
  const response: any = await manageAPICall(AuthAPI.logout.url, AuthAPI.logout.method, {});
  if (response && response.status) {
    notify.success(t('logout_success_msg'));
    store.dispatch(logoutUserState());
  }
  return response;
};
export const resetPasswordAPI = async (data: object) => {
  const response: any = await manageAPICall(
    AuthAPI.resetPassword.url,
    AuthAPI.resetPassword.method,
    data
  );
  return response;
};
export const createPasswordAPI = async (data: object) => {
  const response: any = await manageAPICall(
    AuthAPI.createPassword.url,
    AuthAPI.createPassword.method,
    data
  );
  return response;
};
export const refreshToken = async () => {
  const userInfo: any = userInfoSelector(store.getState());
  if (userInfo) {
    const data = {
      refresh_token: userInfo.refresh_token,
    };

    const response: any = await manageAPICall(
      AuthAPI.refreshToken.url,
      AuthAPI.refreshToken.method,
      data
    );
    if (response && response.status) {
      const updatedObj = { ...userInfo, token: response.data?.token };
      storeUserInfo(updatedObj);
      store.dispatch(setUserAuthInfo(updatedObj));
    }
    return response;
  }
  return null;
};

export const registerFFL = async (data: object | null) => {
  const response = await manageAPICall(AuthAPI.registerFFL.url, AuthAPI.registerFFL.method, data);
  return response;
};
export const registerCustomer = async (data: object | null) => {
  const response = await manageAPICall(
    AuthAPI.registerCustomer.url,
    AuthAPI.registerCustomer.method,
    data
  );
  return response;
};
export const uploadMedia = async (data: object | null) => {
  const response = await manageAPICall(AuthAPI.uploadMedia.url, AuthAPI.uploadMedia.method, data);
  return response;
};
export const updateUserProfile = async (data: object | null) => {
  // Can update user details by passing id
  const response = await manageAPICall(
    AuthAPI.updateUserProfile.url,
    AuthAPI.updateUserProfile.method,
    data
  );
  return response;
};
export const getUserProfile = async (data: object | null) => {
  const response = await manageAPICall(
    AuthAPI.getUserProfile.url,
    AuthAPI.getUserProfile.method,
    data
  );
  return response;
};
export const updateUserStatus = async (data: object | null) => {
  // to update particular user's account status by passing id
  const response = await manageAPICall(
    AuthAPI.updateUserStatus.url,
    AuthAPI.updateUserStatus.method,
    data
  );
  return response;
};

export const updateFastBoundData = async (data: object) => {
  const response = await manageAPICall(
    AuthAPI.updateFastBound.url,
    AuthAPI.updateFastBound.method,
    data
  );
  return response;
};
export const getFastBoundSetting = async () => {
  const response = await manageAPICall(
    AuthAPI.getFastBoundSetting.url,
    AuthAPI.getFastBoundSetting.method,
    {}
  );
  return response;
};
export const updateUserMenuConfig = async (data: object) => {
  const response = await manageAPICall(
    AuthAPI.updateUserMenuConfig.url,
    AuthAPI.updateUserMenuConfig.method,
    data
  );
  return response;
};
export const getUserMenuConfig = async (data: object) => {
  const response = await manageAPICall(
    AuthAPI.getUserMenuConfig.url,
    AuthAPI.getUserMenuConfig.method,
    data
  );
  return response;
};
export const deleteUserMenuConfig = async (data: object) => {
  const response = await manageAPICall(
    AuthAPI.deleteUserMenuConfig.url,
    AuthAPI.deleteUserMenuConfig.method,
    data
  );
  return response;
};
export const buildLoginPayload = (formData: any) => {
  const deviceId = getDeviceId();
  return {
    device_os_version: deviceId,
    device_id: deviceId,
    fcm_token: '',
    device_model: navigator.userAgent,
    device_type: DEVICE_TYPE,
    app_code: APP_CODE,
    ...formData,
  };
};
export const handlePostLogin = (
  data: any,
  options: {
    navigate?: any;
    onClose?: () => void;
    // a customer stays on the page they logged in from (dealers and admins still go to their dashboard)
    stayOnPageForCustomer?: boolean;
  }
) => {
  storeUserInfo(data);
  updateUserState(data);

  // the guest FFL pick is superseded by the account's own address book
  removeGuestDeliveryLocation();
  store.dispatch(setCheckoutSelectedShippingFfl(null));
  // coupons picked as a guest don't carry over to the account
  removeSelectedFflCoupons();

  if (data?.sidebar_menu_config) {
    saveSidebarOrderMap(data.sidebar_menu_config);
  } else {
    clearSidebarOrderMap();
  }

  if (options.onClose) {
    options.onClose();
  }
  if (options.navigate) {
    if (data?.selected_role?.slug === CUSTOMER_ROLE_SLUG) {
      if (!options.stayOnPageForCustomer) {
        options.navigate(customerMainRoutes.customerHome.path);
      }
    } else {
      options.navigate(placeParamsToURL(adminMainRoutes.dashboard.path));
    }
  }
};

export const executeLoginFlow = async (
  formData: any,
  setLoading: (loading: boolean) => void,
  options: {
    navigate?: any;
    onClose?: () => void;
    stayOnPageForCustomer?: boolean;
  }
) => {
  try {
    const apiPayload = buildLoginPayload(formData);
    setLoading(true);

    const response: any = await loginUser(apiPayload);

    if (response && response.status && response?.data?.selected_role) {
      const responseData = response?.data;
      const roleSlugValue = responseData?.selected_role?.slug;

      if ([FFL_ROLE_SLUG, ADMIN_ROLE_SLUG].includes(roleSlugValue)) {
        let actionFunction = getAdminProfileInfo;
        if (roleSlugValue === FFL_ROLE_SLUG) {
          actionFunction = getFFLProfileInfo;
        }

        const profileResponse: any = await actionFunction(responseData?.token);
        if (profileResponse.status) {
          const finalData = {
            ...responseData,
            fflInfo: profileResponse?.data,
          };
          setLoading(false);
          handlePostLogin(finalData, options);
        } else {
          setLoading(false);
          notify.error(profileResponse.message ?? t('something_went_wrong'));
        }
      } else {
        setLoading(false);
        handlePostLogin(responseData, options);
      }
    } else {
      setLoading(false);
      notify.error(response?.message ?? t('something_went_wrong'));
    }
  } catch (err: any) {
    setLoading(false);
    notify.error(err.message ?? t('something_went_wrong'));
  }
};

export const executeFFLVerification = async (
  fflValue: string | undefined | null,
  setLoading: (loading: boolean) => void,
  callbacks: {
    setValue: (field: any, value: any) => void;
    clearErrors?: (field: any) => void;
  }
) => {
  const cleanFFL = fflValue?.replace(FFLNumberCleanREGEX, '');
  if (!cleanFFL || cleanFFL.length !== 15) return;

  try {
    setLoading(true);
    const payload = {
      licsRegn: cleanFFL.slice(0, 1),
      licsDis: cleanFFL.slice(1, 3),
      licsSeq: cleanFFL.slice(-5),
    };
    const response: any = await verifyFFLLicence(payload);

    if (response?.status) {
      const { expiration_date, license_name, trade_name } = response.data || {};
      if (expiration_date) {
        callbacks.setValue('ffl_expiry_date', getServerFormattedDate(expiration_date));
      } else {
        callbacks.setValue('ffl_expiry_date', '');
      }
      if (license_name && callbacks.clearErrors) callbacks.clearErrors('business_name');
      if (trade_name && callbacks.clearErrors) callbacks.clearErrors('first_name');
      callbacks.setValue('business_name', license_name || '');
      callbacks.setValue('first_name', trade_name || '');
    } else {
      // notify.error(response?.message || t('invalid_error_msg', { field_name: t('ffl') }));
    }
  } catch (error: any) {
    notify.error(error.message ?? t('something_went_wrong'));
  } finally {
    setLoading(false);
  }
};

export const executeFFLSignUp = async (
  data: any,
  setLoading: (loading: boolean) => void,
  onSuccess?: () => void
) => {
  try {
    setLoading(true);
    const response: any = await registerFFL(data);
    setLoading(false);

    if (response?.status) {
      largeSuccessMessageWithHTML(
        t('registration_completed_msg'),
        t('registration_completed_instruction_msg'),
        () => {
          if (onSuccess) onSuccess();
        }
      );
      return true;
    } else {
      notify.error(response?.message || t('something_went_wrong'));
      return false;
    }
  } catch (err: any) {
    setLoading(false);
    notify.error(err.message ?? t('something_went_wrong'));
    return false;
  }
};

export const executeCustomerSignUp = async (
  data: any,
  setLoading: (loading: boolean) => void,
  onSuccess?: () => void
) => {
  try {
    setLoading(true);
    const response: any = await registerCustomer(data);
    setLoading(false);

    if (response?.status) {
      successMessageWithHTML(
        t('registration_completed_msg'),
        t('registration_completed_instruction_msg')
      );
      if (onSuccess) onSuccess();
      return true;
    } else {
      notify.error(response?.message || t('something_went_wrong'));
      return false;
    }
  } catch (err: any) {
    setLoading(false);
    notify.error(err.message ?? t('something_went_wrong'));
    return false;
  }
};
